import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { connect } from 'react-redux';
import cn from 'classnames';

import { parseAndCleanHtmlToReact } from 'helpers/HtmlHelpers';
import ProductUtils from 'helpers/ProductUtils';
import { makeSizeCharts } from 'components/productdetail/description/sizeChartBuilder';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import useMartyContext from 'hooks/useMartyContext';

import css from 'styles/components/productdetail/productDescription.scss';

export const ProductDescription = ({ productTitle, descriptionItems, productId, defaultProductType, glossaryLink, isExpanded, allowCollapse, onReportError, showSizeChartLink, showGlossary, showReportError, brandLogo, showSkuBullet = true, gcTerms }) => {
  const { testId } = useMartyContext();
  let featuredDescription;
  let remainingDescriptionItems = [];
  const isGiftCard = ProductUtils.isGiftCard(defaultProductType);
  if (descriptionItems && descriptionItems.bulletPoints.length > 0) {
    featuredDescription = descriptionItems.bulletPoints[0];
    remainingDescriptionItems = descriptionItems.bulletPoints.slice(1);
  }

  return (
    <div className={css.productDescription} itemProp="description" data-test-id={testId('productDescriptionSection')}>
      <h2 className={css.vNeck}><span>{productTitle}</span></h2>
      <div className={cn(css.description, { [css.more]: !isExpanded && allowCollapse && descriptionItems })}>
        {!isGiftCard && <div className={css.brandLogo}>{brandLogo}</div>}
        {descriptionItems ? (
          <ul>
            <div className={css.firstDescriptionItem}>
              {showSkuBullet && <li className={css.descriptionSku} data-test-id={testId('descriptionSku')}>SKU: #{productId}</li>}
              {showSizeChartLink && makeSizeCharts(defaultProductType, descriptionItems, css.viewSizeChart, testId)}
              {featuredDescription && <li className={css.featuredDescription} data-test-id={testId('featuredDescription')}>{parseAndCleanHtmlToReact(featuredDescription)}</li>}
            </div>
            {remainingDescriptionItems.map(
              (item, index) => <li key={`description_${index}`} data-test-id={testId('descriptionItem')}>{parseAndCleanHtmlToReact(item)}</li>)}
            { isGiftCard && gcTerms && <li>{parseAndCleanHtmlToReact(gcTerms)}</li> }
          </ul>
        ) : null}
        {!isGiftCard && showGlossary && (
          <Link
            to={glossaryLink}
            className={css.glossary}
            data-track-action="Product-Page"
            data-track-label="PrDescription"
            data-track-value="View-Glossary"
            data-test-id={testId('glossaryTermsLink')}>
            View Zappos.com Glossary of Terms
          </Link>
        )}
        {showReportError && (
          <div className={css.reportError}>
            Find something wrong in this description?&nbsp;
            <span>Help us fix it!&nbsp;
              <button
                type="button"
                className={css.reportErrorButton}
                onClick={onReportError}
                data-track-action="Product-Page"
                data-track-label="PrDescription"
                data-track-value="Report-An-Error"
                data-test-id={testId('reportAnError')}>
                Report An Error
              </button>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

ProductDescription.propTypes = {
  productTitle: PropTypes.string.isRequired,
  descriptionItems: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string
  ]).isRequired,
  productId: PropTypes.string.isRequired,
  defaultProductType: PropTypes.string.isRequired,
  glossaryLink: PropTypes.string,
  isExpanded: PropTypes.bool,
  allowCollapse: PropTypes.bool,
  showBreadcrumbsSku: PropTypes.bool,
  showSizeChartLink: PropTypes.bool,
  showGlossary: PropTypes.bool,
  showReportError: PropTypes.bool,
  onReportError: PropTypes.func.isRequired
};

ProductDescription.contextTypes = {
  testId: PropTypes.func
};

export const mapStateToProps = state => {
  const {
    headerFooter: { content: { Global: { slotData: { giftCardTerms: { gcTerms } = {} } } } }
  } = state;
  return {
    gcTerms
  };
};

const ProductDescriptionConnected = connect(mapStateToProps)(ProductDescription);

export default withErrorBoundary('ProductDescription', ProductDescriptionConnected);
